import { useId } from "react";
import type { ReactNode } from "react";
import { useModalSurface } from "./useModalSurface";

export interface SheetProps {
  /** Visible title, and the sheet's accessible name. */
  readonly title: ReactNode;
  readonly children: ReactNode;
  readonly onClose: () => void;
  readonly side?: "left" | "right";
  /** Tailwind width class for the panel. Defaults to a reading-width column. */
  readonly widthClass?: string;
  /** Label for the close control, when "Close <title>" reads awkwardly. */
  readonly closeLabel?: string;
}

/**
 * A side sheet: a dialog anchored to an edge of the viewport, for content that
 * lives beside the page on a wide screen and cannot fit next to it on a narrow
 * one.
 *
 * It shares {@link useModalSurface} with {@link Dialog}, so a sheet is not a
 * lesser surface — focus enters it, Tab stays inside it, Escape leaves it, and
 * focus returns to whatever opened it.
 *
 * Motion is opt-in via `prefers-reduced-motion`: the panel slides in unless the
 * reader has asked their system not to animate, in which case it simply appears.
 * That preference is usually set for a reason (vestibular discomfort, or a
 * machine where animation costs frames), and a sheet is large enough on screen
 * for the difference to matter.
 */
export function Sheet({
  title,
  children,
  onClose,
  side = "right",
  widthClass = "w-full max-w-md",
  closeLabel,
}: SheetProps) {
  const titleId = useId();
  const panelRef = useModalSurface({ onClose });

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0"
        style={{
          backgroundColor:
            "color-mix(in oklab, var(--color-ink-950) 60%, transparent)",
        }}
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`sheet-panel absolute inset-y-0 ${
          side === "right"
            ? "right-0 sheet-panel--right"
            : "left-0 sheet-panel--left"
        } flex ${widthClass} flex-col`}
        style={{
          backgroundColor: "var(--bg-sidebar)",
          [side === "right" ? "borderLeft" : "borderRight"]:
            "1px solid var(--border)",
        }}
      >
        <div
          className="flex h-12 shrink-0 items-center justify-between border-b px-4"
          style={{ borderColor: "var(--border)" }}
        >
          <span
            id={titleId}
            className="text-sm font-semibold"
            style={{ color: "var(--fg)" }}
          >
            {title}
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label={closeLabel ?? "Close"}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </div>
    </div>
  );
}
